import { getFrameTypeIcon } from "@/components/ui/concept-icons";
import { useShallow } from "zustand/react/shallow";
import { useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import { frameTypeLabel } from "@/components/shapes/shared/frame-type-label";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icons,
} from "@/components/ui";
import { presentArtifactBesideConversation } from "@/lib/conversation-artifact-presentation";
import { useFramesStore } from "@/stores/frames-store";
import { selectOrderedFrames } from "@/stores/frames-selectors";
import { useConversationStore } from "@/stores/conversation-store";
import {
  resolveArtifactsForExecutionIds,
  resolveConversationExecutionIds,
} from "./conversation-artifact-resolver";
import type { FrameData } from "@/lib/frame-data";

interface ConversationArtifactMenuProps {
  conversationFrameId: string;
  frame: FrameData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onArtifactPresented?: () => void;
  testIdFrameId?: string;
  hideWhenEmpty?: boolean;
}

export function ConversationArtifactMenu({
  conversationFrameId,
  frame,
  open,
  onOpenChange,
  onArtifactPresented,
  testIdFrameId = conversationFrameId,
  hideWhenEmpty = false,
}: ConversationArtifactMenuProps) {
  const t = useT(framesConversationMessages);
  const executionIds = useConversationStore(
    useShallow((state) =>
      resolveConversationExecutionIds(
        conversationFrameId,
        frame,
        state.messages,
      ),
    ),
  );
  const artifacts = useFramesStore(
    useShallow((state) =>
      resolveArtifactsForExecutionIds(executionIds, selectOrderedFrames(state)),
    ),
  );

  if (hideWhenEmpty && artifacts.length === 0) return null;

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="iconSm"
          aria-label={t("connectedFrames", { count: artifacts.length })}
          aria-expanded={open}
          className="w-auto min-w-6 gap-0.5 px-1"
          data-testid={`conversation-artifacts-${testIdFrameId}`}
        >
          <Icons.Frame className="h-3.5 w-3.5" />
          {artifacts.length > 0 ? (
            <span
              className="flex h-3.5 min-w-3.5 items-center justify-center rounded-full border border-glass-stroke px-0.5 text-appkit-mini font-medium leading-none text-muted"
              aria-hidden="true"
              data-testid={`conversation-artifacts-count-${testIdFrameId}`}
            >
              {artifacts.length}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="min-w-64">
        {artifacts.length === 0 ? (
          <div
            className="px-appkit-sm py-appkit-xs text-appkit-body text-muted"
            data-testid={`conversation-artifacts-empty-${testIdFrameId}`}
          >
            {t("noConnectedFrames")}
          </div>
        ) : (
          artifacts.map((artifact) => {
            const ArtifactIcon = getFrameTypeIcon(artifact.frameType);
            return (
              <DropdownMenuItem
                key={artifact.frameId}
                icon={<ArtifactIcon />}
                trailing={
                  <span className="text-appkit-caption text-muted">
                    {frameTypeLabel(artifact.frameType)}
                  </span>
                }
                onSelect={() => {
                  const presented = presentArtifactBesideConversation(
                    conversationFrameId,
                    artifact.frameId,
                  );
                  if (presented) onArtifactPresented?.();
                }}
                data-testid={`conversation-artifact-${testIdFrameId}-${artifact.frameId}`}
              >
                {artifact.title.trim() || t("untitledArtifact")}
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
